import { useState } from "react";
import { useRecordContext } from "ra-core";
import {
  Calendar,
  CheckSquare,
  CreditCard,
  TrendingUp,
  User,
} from "lucide-react";

import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";

import { ContactTimeline } from "./ContactTimeline";
import { ContactTasksList } from "./ContactTasksList";
import { ContactPayments } from "./ContactPayments";
import { ContactActivityTab } from "./ContactActivityTab";
import { ContactDetailsTab } from "./ContactDetailsTab";
import type { Contact } from "../types";

type TabKey = "timeline" | "activite" | "paiements" | "taches" | "details";

const TABS: { key: TabKey; label: string; Icon: typeof Calendar }[] = [
  { key: "timeline", label: "Timeline", Icon: Calendar },
  { key: "activite", label: "Activité", Icon: TrendingUp },
  { key: "paiements", label: "Paiements", Icon: CreditCard },
  { key: "taches", label: "Tâches", Icon: CheckSquare },
  { key: "details", label: "Détails", Icon: User },
];

/**
 * 5 onglets de la fiche contact refondue. Sprint 2 · T2.3.
 * Cf design/atomic-crm/app.jsx (tabs-row).
 *
 * Lazy loading : chaque TabsContent rend ses enfants uniquement quand
 * l'onglet est actif (évite 4 useGetList simultanés au mount).
 *
 * T2.3.a : Timeline (Tâche 2.4) + Paiements + Tâches opérationnels.
 *          Activité et Détails sont des placeholders T2.3.c.
 */
export function ContactTabs() {
  const record = useRecordContext<Contact>();
  const [active, setActive] = useState<TabKey>("timeline");
  const nbTasks = record?.nb_tasks ?? 0;

  return (
    <Tabs
      value={active}
      onValueChange={(v) => setActive(v as TabKey)}
      className="w-full"
    >
      <TabsList className="bg-transparent border-b border-border rounded-none w-full justify-start h-auto p-0 gap-0">
        {TABS.map((t) => {
          const Ic = t.Icon;
          const showCount = t.key === "taches" && nbTasks > 0;
          return (
            <TabsTrigger
              key={t.key}
              value={t.key}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2.5 gap-1.5 text-[13px]"
            >
              <Ic className="size-3.5" />
              {t.label}
              {showCount && (
                <span className="ml-1 text-[10.5px] tabular-nums text-muted-foreground">
                  {nbTasks}
                </span>
              )}
            </TabsTrigger>
          );
        })}
      </TabsList>

      <TabsContent value="timeline" className="mt-4">
        {active === "timeline" && <ContactTimeline />}
      </TabsContent>

      <TabsContent value="activite" className="mt-4">
        {active === "activite" && <ContactActivityTab />}
      </TabsContent>

      <TabsContent value="paiements" className="mt-4">
        {active === "paiements" && (
          <Card>
            <CardContent className="py-5">
              <ContactPayments />
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="taches" className="mt-4">
        {active === "taches" && (
          <Card>
            <CardContent className="py-5">
              <ContactTasksList />
            </CardContent>
          </Card>
        )}
      </TabsContent>

      <TabsContent value="details" className="mt-4">
        {active === "details" && <ContactDetailsTab />}
      </TabsContent>
    </Tabs>
  );
}

// Placeholders T2.3.a supprimés : Activité et Détails sont désormais
// livrés par ContactActivityTab et ContactDetailsTab (T2.3.c).
